import { useCallback, useEffect, useRef, useState } from "react";
import type { DeliverySummary, MeResponse } from "../../api/client";

export type DeliveryAction = "review" | "retry_max" | null;

export const deliveryActionFor = (
  delivery: DeliverySummary,
  admin: boolean
): DeliveryAction => {
  if (delivery.telegramState === "needs_review") return "review";
  if (
    admin &&
    delivery.telegramState === "sent" &&
    delivery.maxRequested &&
    delivery.maxState === "failed"
  ) {
    return "retry_max";
  }
  return null;
};

const telegramLabels: Record<DeliverySummary["telegramState"], string> = {
  scheduled: "Запланировано",
  sending: "Отправляется",
  sent: "Опубликовано",
  failed: "Не отправлено",
  needs_review: "Нужна ручная проверка"
};

const maxLabels: Record<NonNullable<DeliverySummary["maxState"]>, string> = {
  not_requested: "Не запрашивалось",
  pending: "Ожидает Telegram",
  sending: "Отправляется",
  sent: "Опубликовано",
  failed: "Ошибка доставки"
};

const formatDate = (value: string | null) => {
  if (!value) return "—";
  let normalized = value;
  if (normalized.startsWith("interval:")) {
    normalized = normalized.slice("interval:".length);
  } else if (normalized.startsWith("daily:")) {
    const match = /^daily:(\d{4}-\d{2}-\d{2}):(\d{2}:\d{2})$/.exec(normalized);
    if (match) normalized = `${match[1]}T${match[2]}:00`;
  }
  const date = new Date(normalized);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      }).format(date);
};

const telegramMessageLink = (delivery: DeliverySummary) => {
  const messageId = delivery.telegramMessageIds[0];
  const chat = String(delivery.chatId);
  if (!messageId || !chat.startsWith("-100")) return null;
  return `https://t.me/c/${chat.slice(4)}/${messageId}`;
};

type DeliveryApi = {
  loadDeliveries: (signal?: AbortSignal) => Promise<DeliverySummary[]>;
  retryMaxDelivery: (
    id: string,
    requestId: string,
    signal?: AbortSignal
  ) => Promise<Record<string, unknown>>;
  markDeliveryPublished: (
    id: string,
    signal?: AbortSignal
  ) => Promise<DeliverySummary>;
  rescheduleDelivery: (
    id: string,
    dailyTime: string,
    requestId: string,
    signal?: AbortSignal
  ) => Promise<Record<string, unknown>>;
};

export function DeliveryCenter({
  capabilities,
  api,
  onItemsChange
}: {
  capabilities: MeResponse["capabilities"];
  api: DeliveryApi;
  onItemsChange?: (items: DeliverySummary[]) => void;
}) {
  const [items, setItems] = useState<DeliverySummary[] | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rescheduleAt, setRescheduleAt] = useState<Record<string, string>>({});
  const loadVersion = useRef(0);
  const actionVersion = useRef(0);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const version = ++loadVersion.current;
      try {
        const next = await api.loadDeliveries(signal);
        if (!signal?.aborted && version === loadVersion.current) {
          const sorted = [...next].sort((left, right) => {
            const priority = Number(deliveryActionFor(left, capabilities.admin) === null) -
              Number(deliveryActionFor(right, capabilities.admin) === null);
            if (priority !== 0) return priority;
            return Date.parse(right.createdAt) - Date.parse(left.createdAt);
          });
          setItems(sorted);
          onItemsChange?.(sorted);
        }
      } catch (loadError) {
        if (!signal?.aborted && version === loadVersion.current) {
          setItems([]);
          setError("Не удалось загрузить историю доставок.");
        }
      }
    },
    [api, capabilities.admin, onItemsChange]
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const runAction = async (
    delivery: DeliverySummary,
    action: "retry_max" | "mark" | "reschedule"
  ) => {
    if (pendingId) return;
    const version = ++actionVersion.current;
    const controller = new AbortController();
    setPendingId(delivery.id);
    setError(null);
    try {
      if (action === "retry_max") {
        await api.retryMaxDelivery(
          delivery.id,
          crypto.randomUUID(),
          controller.signal
        );
      } else if (action === "mark") {
        await api.markDeliveryPublished(delivery.id, controller.signal);
      } else {
        const localValue = rescheduleAt[delivery.id];
        if (!localValue) {
          setError("Выберите новую дату и время публикации.");
          return;
        }
        await api.rescheduleDelivery(
          delivery.id,
          new Date(localValue).toISOString(),
          crypto.randomUUID(),
          controller.signal
        );
      }
      if (version === actionVersion.current) await load(controller.signal);
    } catch {
      if (version === actionVersion.current) {
        setError(
          action === "retry_max"
            ? "Повтор MAX не завершён. Telegram-публикация сохранена."
            : "Действие не выполнено. Обновите историю и проверьте статус."
        );
      }
    } finally {
      if (version === actionVersion.current) setPendingId(null);
    }
  };

  if (items === null) return <div className="skeleton list-skeleton" />;
  if (items.length === 0) {
    return (
      <div className="empty-state">
        <strong>История пока пуста</strong>
        <p>Здесь появятся фактические результаты ближайших публикаций.</p>
      </div>
    );
  }

  return (
    <div className="delivery-center">
      {error && <p className="inline-error" role="alert">{error}</p>}
      {items.map((delivery) => {
        const action = deliveryActionFor(delivery, capabilities.admin);
        const messageLink = telegramMessageLink(delivery);
        return (
          <article className="delivery-card" key={delivery.id}>
            <header>
              <div>
                <strong>{delivery.postKind === "custom" ? "Свой пост" : "Прайс-пост"}</strong>
                <small>План: {formatDate(delivery.scheduledSlot)}</small>
              </div>
              <span className={`delivery-state ${delivery.telegramState}`}>
                {telegramLabels[delivery.telegramState]}
              </span>
            </header>

            <div className="delivery-timeline">
              <div>
                <span className="timeline-dot telegram" />
                <p><strong>Telegram</strong><small>{telegramLabels[delivery.telegramState]} · {formatDate(delivery.telegramSentAt ?? delivery.telegramFailedAt ?? delivery.telegramNeedsReviewAt)}</small></p>
                {delivery.emojiRepresentation === "unicode_fallback" && <small role="status">Анимированные эмоджи заменены на обычные</small>}
                {messageLink && <a href={messageLink} target="_blank" rel="noreferrer">Открыть сообщение</a>}
              </div>
              {capabilities.admin && delivery.maxRequested && delivery.maxState && (
                <div>
                  <span className="timeline-dot max" />
                  <p><strong>MAX</strong><small>{maxLabels[delivery.maxState]} · {formatDate(delivery.maxSentAt ?? delivery.maxFailedAt ?? delivery.maxStartedAt)}</small></p>
                  {delivery.maxMessageRef && <b>№ {delivery.maxMessageRef}</b>}
                </div>
              )}
            </div>

            {action === "retry_max" && (
              <section className="safe-action-card">
                <div><strong>Безопасный повтор</strong><p>Повторится только MAX. Telegram не затрагивается.</p></div>
                <button type="button" disabled={pendingId !== null} onClick={() => void runAction(delivery, "retry_max")}>{pendingId === delivery.id ? "Повторяем…" : "Повторить MAX"}</button>
              </section>
            )}

            {action === "review" && (
              <section className="safe-action-card review">
                <div><strong>Проверьте канал вручную</strong><p>Telegram не будет отправлен повторно автоматически.</p></div>
                <label><span>Новое время, если поста нет</span><input type="datetime-local" value={rescheduleAt[delivery.id] ?? ""} onChange={(event) => setRescheduleAt((current) => ({ ...current, [delivery.id]: event.target.value }))} /></label>
                <div className="safe-action-buttons"><button type="button" disabled={pendingId !== null} onClick={() => void runAction(delivery, "mark")}>Пост опубликован</button><button type="button" disabled={pendingId !== null} onClick={() => void runAction(delivery, "reschedule")}>Перенести</button></div>
              </section>
            )}

            <details>
              <summary>Технические детали</summary>
              <dl><div><dt>Код</dt><dd>{delivery.errorCode ?? "—"}</dd></div><div><dt>Эмоджи</dt><dd>{delivery.emojiRepresentation ?? "—"}{delivery.emojiFallbackCode ? ` · ${delivery.emojiFallbackCode}` : ""}</dd></div><div><dt>ID доставки</dt><dd>{delivery.id}</dd></div></dl>
            </details>
          </article>
        );
      })}
    </div>
  );
}
